import React from 'react';
import styled from 'styled-components';
import {
  CpWidgetVariantType,
  CustomerPortalWidgetType,
} from 'api/customer-portal/types';
import { Loading } from 'components/atoms/loading';
import { CpCtaLoading } from './cp-cta-loading/cp-cta-loading';
import { CpFAQLoading } from './cp-faq-loading/cp-faq-loading';
import { CpFeatureLoading } from './cp-feature-loading/cp-feature-loading';
import { CpFooterLoading } from './cp-footer-loading/cp-footer-loading';
import { CpHeroSectionLoading } from './cp-hero-section-loading/cp-hero-section-loading';
import { CpImageGalleryLoading } from './cp-image-gallery-loading/cp-image-gallery-loading';
import { CpLogoSkeleton } from './cp-logo-skeleton/cp-logo-skeleton';
import { CpMetricsLoading } from './cp-metrics-loading/cp-metrics-loading';
import { CpProjectFormsLoading } from './cp-project-forms-loading/cp-project-forms-loading';
import { CpProjectOverviewLoading } from './cp-project-overview-loading/cp-project-overview-loading';
import { CpProjectPlanLoading } from './cp-project-plan-loading/cp-project-plan-loading';
import { CpProjectTasksListLoading } from './cp-project-tasks-list-loading/cp-project-tasks-list-loading';
import { CpProjectUpdatesLoading } from './cp-project-updates-loading/cp-project-updates-loading';
import { CpResourcesLoading } from './cp-resources-loading/cp-resources-loading';
import { CpSpacesAndFilesLoadingSkeleton } from './cp-spaces-and-files-loading/cp-spaces-and-files';
import { CpSummaryInformationLoadingSkeleton } from './cp-summary-info-loading/cp-summary-info-loading';
import { CpTeamMembersLoadingSkeleton } from './cp-team-members-loading/cp-team-members-loading';
import { CpVideosLoading } from './cp-videos-loading/cp-videos-loading';

export interface CpWidgetLoadingProps {
  widgetType: CustomerPortalWidgetType;
  variantType: CpWidgetVariantType;
  styles?: Record<string, unknown>;
  input: Record<string, unknown>;
}

const LoadingWidgetWrapper = styled.div`
  width: 100%;
  height: 15rem;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--cp-white);
`;

export const CpWidgetLoadingSkeletons: React.FC<CpWidgetLoadingProps> = ({
  widgetType,
  variantType,
  styles,
  input,
}) => {
  if (!widgetType || !variantType) {
    return <></>;
  }

  switch (widgetType) {
    case CustomerPortalWidgetType.CHATS:
      return (
        <LoadingWidgetWrapper>
          <Loading size="large" />
        </LoadingWidgetWrapper>
      );

    case CustomerPortalWidgetType.AccountSummaryInformation:
    case CustomerPortalWidgetType.ProjectSummaryInformation:
    case CustomerPortalWidgetType.ProjectHours:
      return (
        <CpSummaryInformationLoadingSkeleton
          variantType={variantType}
          styles={styles}
          input={input}
        />
      );
    case CustomerPortalWidgetType.ProjectOverview:
      return (
        <CpProjectOverviewLoading
          input={input}
          styles={styles}
          variantType={variantType}
        />
      );

    case CustomerPortalWidgetType.ProjectPlan:
      return (
        <CpProjectPlanLoading
          input={input}
          styles={styles}
          variantType={variantType}
        />
      );

    case CustomerPortalWidgetType.ProjectForms:
      return (
        <CpProjectFormsLoading
          input={input}
          styles={styles}
          variantType={variantType}
        />
      );

    case CustomerPortalWidgetType.TeamMembers:
      return (
        <CpTeamMembersLoadingSkeleton
          input={input}
          styles={styles}
          variantType={variantType}
        />
      );

    case CustomerPortalWidgetType.ProjectUpdates:
      return (
        <CpProjectUpdatesLoading
          input={input}
          styles={styles}
          variantType={variantType}
        />
      );

    case CustomerPortalWidgetType.LogoWidget:
      return (
        <CpLogoSkeleton
          input={input}
          styles={styles}
          variantType={variantType}
        />
      );

    case CustomerPortalWidgetType.HeroSection:
      return (
        <CpHeroSectionLoading
          input={input}
          styles={styles}
          variantType={variantType}
        />
      );

    case CustomerPortalWidgetType.Metrics:
      return (
        <CpMetricsLoading
          input={input}
          styles={styles}
          variantType={variantType}
        />
      );

    case CustomerPortalWidgetType.Feature:
      return (
        <CpFeatureLoading
          input={input}
          styles={styles}
          variantType={variantType}
        />
      );

    case CustomerPortalWidgetType.FAQ:
      return (
        <CpFAQLoading input={input} styles={styles} variantType={variantType} />
      );

    case CustomerPortalWidgetType.ImageGallery:
      return (
        <CpImageGalleryLoading
          input={input}
          styles={styles}
          variantType={variantType}
        />
      );

    case CustomerPortalWidgetType.Resources:
      return (
        <CpResourcesLoading
          input={input}
          styles={styles}
          variantType={variantType}
        />
      );

    case CustomerPortalWidgetType.VideoList:
      return (
        <CpVideosLoading
          input={input}
          styles={styles}
          variantType={variantType}
        />
      );

    case CustomerPortalWidgetType.CTA:
      return (
        <CpCtaLoading input={input} styles={styles} variantType={variantType} />
      );

    case CustomerPortalWidgetType.Footer:
      return (
        <CpFooterLoading
          input={input}
          styles={styles}
          variantType={variantType}
        />
      );

    case CustomerPortalWidgetType.AccountProjectsList:
    case CustomerPortalWidgetType.ProjectTasksList:
      return (
        <CpProjectTasksListLoading
          input={input}
          styles={styles}
          variantType={variantType}
        />
      );

    case CustomerPortalWidgetType.AccountSpaces:
    case CustomerPortalWidgetType.SPACE:
    case CustomerPortalWidgetType.AllFiles:
    case CustomerPortalWidgetType.AccountFiles:
      return (
        <CpSpacesAndFilesLoadingSkeleton
          input={input}
          styles={styles}
          variantType={variantType}
        />
      );
    default:
      return <></>;
  }
};
